import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card";
import { useState, useEffect } from "react";
import { LoginForm } from "@/components/auth/LoginForm";
import { RegisterForm } from "@/components/auth/RegisterForm";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [isLogin, setIsLogin] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isSendingMagicLink, setIsSendingMagicLink] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState("");
  const [magicLinkDialogOpen, setMagicLinkDialogOpen] = useState(false);
  const [isGoogleSignInLoading, setIsGoogleSignInLoading] = useState(false);

  useEffect(() => {
    // Check for error parameters in hash or search params
    const hashParams = new URLSearchParams(location.hash.replace(/^#/, ''));
    const searchParams = new URLSearchParams(location.search);
    
    const errorCode = hashParams.get('error_code') || searchParams.get('error_code');
    const errorDescription = hashParams.get('error_description') || searchParams.get('error_description');
    
    if (errorCode) {
      console.error('Authentication error detected:', errorCode, errorDescription);
      let errorMessage = errorDescription || 'An authentication error occurred.';
      
      if (errorCode === 'otp_expired') {
        errorMessage = 'The verification link has expired. Please request a new one below.';
      }
      
      setError(errorMessage);
      
      // Clean the URL without reloading the page
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location]);

  useEffect(() => {
    const checkSession = async () => {
      try {
        console.log('Checking session...');
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session check error:', sessionError);
          await supabase.auth.signOut();
          return;
        }

        if (session) {
          console.log('Active session found, redirecting to dashboard');
          navigate("/");
        }
      } catch (err) {
        console.error('Error during session check:', err);
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state changed:', event, session ? 'Session exists' : 'No session');
      
      if (event === 'SIGNED_IN' && session) {
        console.log('User signed in successfully, redirecting...');
        navigate("/");
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [navigate]);

  const handleSubmit = async (data: any) => {
    try {
      setError(null);
      console.log('Attempting authentication...');
      
      if (isLogin) {
        console.log('Signing in user:', data.email);
        const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
          email: data.email,
          password: data.password,
        });

        if (signInError) {
          console.error('Sign in error:', signInError);
          
          if (signInError.message.includes('Database error')) {
            setError('There was an issue connecting to the service. Please try again later.');
            return;
          }
          
          if (signInError.message.includes('Invalid login credentials')) {
            setError('Invalid email or password. Please check your credentials and try again.');
            return;
          }
          
          throw signInError;
        }

        console.log('Sign in successful:', authData);
        toast({
          title: "Login successful",
          description: "Welcome to DIYLegal!",
        });
        
        navigate("/");
      } else {
        console.log('Registering new user:', data.email);
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email: data.email,
          password: data.password,
          options: {
            data: {
              name: data.name,
              age: data.age,
              companyName: data.companyName,
              phone: data.phone,
            },
            emailRedirectTo: `${window.location.origin}/verification-success`,
          },
        });

        if (signUpError) {
          console.error('Sign up error:', signUpError);
          throw signUpError;
        }

        if (signUpData.user && !signUpData.user.email_confirmed_at) {
          toast({
            title: "Registration successful",
            description: "Please check your email to verify your account.",
          });
          setError('A verification email has been sent. Please check your inbox and spam folder to verify your account before signing in.');
        }
      }
    } catch (error) {
      console.error('Authentication error:', error);
      const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
      setError(errorMessage);
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    }
  };

  const handleForgotPassword = () => {
    setResetPasswordOpen(true);
  };

  const handleResetPassword = async () => {
    if (!resetEmail || !resetEmail.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsResettingPassword(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/password-reset`,
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Password reset email sent",
        description: "Please check your email to reset your password.",
      });
      setResetPasswordOpen(false);
    } catch (error) {
      console.error('Password reset error:', error);
      const errorMessage = error instanceof Error ? error.message : "Failed to send password reset email.";
      setError(errorMessage);
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleResendVerificationEmail = async () => {
    if (!magicLinkEmail || !magicLinkEmail.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsSendingMagicLink(true);
    setError(null);

    try {
      const { error } = await supabase.auth.signInWithOtp({
        email: magicLinkEmail,
        options: {
          emailRedirectTo: `${window.location.origin}/verification-success`,
        },
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Verification email sent",
        description: "Please check your email for the verification link.",
      });
      setMagicLinkDialogOpen(false);
    } catch (error) {
      console.error('Verification email error:', error);
      const errorMessage = error instanceof Error ? error.message : "Failed to send verification email.";
      setError(errorMessage);
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    } finally {
      setIsSendingMagicLink(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError(null);
      setIsGoogleSignInLoading(true);
      console.log('Initiating Google sign-in...');
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
        }
      });

      if (error) {
        console.error('Google sign-in error:', error);
        setError(`Google sign-in failed: ${error.message}`);
        toast({
          variant: "destructive",
          title: "Google Sign-In Error",
          description: error.message,
        });
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to sign in with Google';
      setError(errorMessage);
      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    } finally {
      setIsGoogleSignInLoading(false);
    }
  };

  // Check for expired link error and show appropriate UI
  const showResendVerificationOption = error?.includes('verification link has expired');

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <div className="flex justify-center">
            <img
              src="/diy-legal-logo.png"
              alt="DIYLegal Logo"
              className="h-24 w-auto mb-6"
              onError={(e) => {
                console.error('Error loading logo:', e);
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
          <CardDescription className="text-center">
            Your Legal Document Management Solution
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-6 grid grid-cols-2 rounded-lg bg-muted p-1">
            <Button
              type="button"
              variant={isLogin ? "default" : "ghost"}
              onClick={() => {
                setIsLogin(true);
                setError(null);
              }}
            >
              Login
            </Button>
            <Button
              type="button"
              variant={!isLogin ? "default" : "ghost"}
              onClick={() => {
                setIsLogin(false);
                setError(null);
              }}
            >
              Sign Up
            </Button>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
              {showResendVerificationOption && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2 w-full"
                  onClick={() => setMagicLinkDialogOpen(true)}
                >
                  Resend Verification Email
                </Button>
              )}
            </Alert>
          )}
          {isLogin ? (
            <>
              <LoginForm 
                onSubmit={handleSubmit} 
                onForgotPassword={handleForgotPassword}
              />
              <p className="mt-4 text-center text-sm text-muted-foreground">
                New here?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(false);
                    setError(null);
                  }}
                  className="font-medium text-primary hover:underline"
                >
                  Sign up with email
                </button>
              </p>
            </>
          ) : (
            <>
              <RegisterForm onSubmit={handleSubmit} />
              <p className="mt-4 text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setIsLogin(true);
                    setError(null);
                  }}
                  className="font-medium text-primary hover:underline"
                >
                  Login
                </button>
              </p>
            </>
          )}
          
          {/* Google Sign-In Section */}
          {/* <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">
                  Or continue with
                </span>
              </div>
            </div>
            <Button
              variant="outline"
              type="button"
              className="w-full mt-4"
              onClick={handleGoogleSignIn}
              disabled={isGoogleSignInLoading}
            >
              {isGoogleSignInLoading ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"></div>
                  Signing in...
                </>
              ) : (
                <>
                  <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  Continue with Google
                </>
              )}
            </Button>
          </div> */}

          {/* <div className="text-center mt-4">
            <button
              type="button"
              onClick={() => {
                setIsLogin(!isLogin);
                setError(null);
              }}
              className="text-blue-600 hover:text-blue-800"
            >
              {isLogin
                ? "Need an account? Register"
                : "Already have an account? Sign in"}
            </button>
          </div> */}
        </CardContent>
      </Card>

      {/* Password Reset Dialog */}
      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              type="email"
              placeholder="Email address"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              className="w-full"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResetPasswordOpen(false)}
              disabled={isResettingPassword}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleResetPassword}
              disabled={isResettingPassword}
            >
              {isResettingPassword ? "Sending..." : "Send Reset Link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verification Email Dialog */}
      <Dialog open={magicLinkDialogOpen} onOpenChange={setMagicLinkDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {showResendVerificationOption ? "Resend Verification Email" : "Sign In with Magic Link"}
            </DialogTitle>
            <DialogDescription>
              {showResendVerificationOption 
                ? "Enter your email address and we'll send you a new verification link."
                : "Enter your email address and we'll send you a magic link to sign in instantly."}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              type="email"
              placeholder="Email address"
              value={magicLinkEmail}
              onChange={(e) => setMagicLinkEmail(e.target.value)}
              className="w-full"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMagicLinkDialogOpen(false)}
              disabled={isSendingMagicLink}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleResendVerificationEmail}
              disabled={isSendingMagicLink}
            >
              {isSendingMagicLink ? "Sending..." : (showResendVerificationOption ? "Resend Verification" : "Send Magic Link")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Auth;
